import React, { useContext, useEffect, useState } from 'react';
import clsx from 'clsx';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';

import Layout from '../../common_components/Layout';
import InnerModal from '../components/InnerModal';
import { AppContext } from '../contexts';
import { getDataForStep } from '../utils';
import Loading from '../../common_components/Loading';

export default function DataImportSummary({ data, isCompleted }) {
  const app = useContext(AppContext);
  const stepsApi = app.APIs.steps;

  const warnings = data && data['warnings'];
  const errors = data && data['errors'];
  const hasErrors = errors?.present;
  const hasWarnings = warnings?.present;
  const missingData = warnings?.data.missing;
  const errorColumns = warnings?.data['columns_with_errors'];
  const numberOfDuplicates = errors?.data['duplicates'];
  const dataTooShort = errors?.data['too_short'];

  const dataSummary = data && data['summary']['data'];
  const columnsDescriptions = data && data['summary']['columns_descriptions'];
  const columnsTranslations = data && data['summary']['column_translation'];
  const columnsNames = data && data['summary']['columns_order'];

  const [isLoading, setIsLoading] = useState(!data);
  const [continueAlertOpen, setContinueAlertOpen] = useState(false);

  const dataimportInfo = getDataForStep('dataimport', app.activeProject.steps);

  const handleContinueBtn = () => {
    setContinueAlertOpen(true);
  };
  const handleGoBackBtn = () => {
    app.setVisibleStep('dataimport');
  };
  const handleVisualizationBtn = () => {
    app.setVisibleStep('visualization');
  };
  const handleModelBtn = () => {
    app.setVisibleStep('modeltraining');
  };

  useEffect(() => {
    const setUpSummaryData = async () => {
      stepsApi.importSummaryInit(app.activeProject.id).then((projectData) => {
        app.setActiveProject(projectData);
        setIsLoading(false);
      });
    };

    if (!data) {
      setUpSummaryData();
    }
  }, []);

  return (
    <Layout
      sideContent={
        <>
          <h2 className="text-white">Dane projektu:</h2>
          <ul className="list-none bg-slate-500 basis-2/3 rounded-md p-4">
            <li>Nazwa: {app.activeProject.name}</li>
            <li>Stacja: {dataimportInfo.stationname}</li>
            <li>Kod stacji: {dataimportInfo.stationcode}</li>
            <li className="break-words break-all leading-5">
              Analizowany plik: <br />
              {app.activeProject.datafile}
            </li>
            <li>Nazwa substancji: {dataimportInfo.substancename}</li>
            <li>Jednostka: {dataimportInfo.substanceunit}</li>
          </ul>
          {isCompleted && (
            <div className="flex flex-col gap-4">
              <button className="btn" onClick={handleGoBackBtn}>
                Wczytaj ponownie plik
              </button>
            </div>
          )}
        </>
      }
      title="Podsumowanie importu danych"
      mainContent={
        <>
          <div className="flex flex-col h-full w-full items-center justify-center relative">
            {isLoading ? (
              <Loading />
            ) : (
              <>
                <div
                  className="overflow-x-auto max-w-full"
                  tabIndex={0}
                  role="region"
                  aria-label="Podsumowanie danych – przewijana tabela"
                >
                  {dataSummary && (
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          {columnsNames.map((column, i) => (
                            <th key={column + i}>
                              {columnsDescriptions?.[column] ? (
                                <Tippy
                                  content={columnsDescriptions[column]}
                                  placement={
                                    i > columnsNames.length / 2
                                      ? 'left'
                                      : 'right'
                                  }
                                  theme="custom-dark"
                                >
                                  <span className="cursor-help whitespace-normal text-left">
                                    {columnsTranslations?.[column]}
                                  </span>
                                </Tippy>
                              ) : (
                                <span>{column}</span>
                              )}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {dataSummary.map((row, i) => (
                          <tr
                            className={clsx(
                              row.verification == 'error' && 'bg-warning',
                            )}
                            key={row.variable + i}
                          >
                            {columnsNames.map((column, i) => (
                              <td key={column + i}>
                                {column == 'nan_perc'
                                  ? preFormatNanPercent(row[column])
                                  : row[column]}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
                {!hasErrors && !hasWarnings && (
                  <div>
                    <div className="text-success font-semibold text-lg">
                      Sprawdzenie szeregu czasowego:
                    </div>
                    <ol>
                      <li>
                        W szeregu czasowym nie występują powtórzenia daty.
                      </li>
                      <li>
                        Szereg czasowy jest kompletny – nie stwierdzono
                        brakujących obserwacji.
                      </li>
                    </ol>
                  </div>
                )}
                {hasErrors && (
                  <div>
                    <div className="bg-error rounded inline-block px-1 -ml-1 font-semibold">
                      Błąd
                    </div>
                    <ol>
                      {numberOfDuplicates > 0 && (
                        <li>
                          W szeregu czasowym występują powtórzenia daty. Sprawdź
                          plik danych. Usuń zbędne powtarzające się rekordy
                          daty.
                        </li>
                      )}
                      {dataTooShort && <li>Podane dane są zbyt krótkie.</li>}
                    </ol>
                  </div>
                )}
                {hasWarnings && (
                  <div>
                    <div className="bg-warning rounded inline-block px-1 -ml-1 font-semibold">
                      Ostrzeżenie
                    </div>
                    <ol>
                      {missingData > 0 && (
                        <li>
                          Szereg czasowy jest niekompletny. Brakuje{' '}
                          {missingData}% obserwacji.
                        </li>
                      )}
                      {errorColumns?.length > 0 && (
                        <li>
                          W kolumnach:{' '}
                          <span className="underline font-bold">
                            {errorColumns
                              .map(({ variable: column }) => column)
                              .join(', ')}{' '}
                          </span>
                          znaleziono błędy w danych, popraw dane i wczytaj plik
                          jeszcze raz lub kontynuuj z błędami. Zakres danych
                          jest pewnym przybliżeniem, w przypadku zmiany
                          jednostki zakres danych może być inny.
                        </li>
                      )}
                    </ol>
                  </div>
                )}
                <div className="flex flex-row gap-4 w-full justify-center mt-8">
                  {(hasWarnings || hasErrors) && !isCompleted ? (
                    <>
                      <button
                        className="btn btn-primary"
                        onClick={handleGoBackBtn}
                      >
                        Powrót
                      </button>
                      <button
                        onClick={handleContinueBtn}
                        className={clsx(
                          'btn',
                          hasWarnings ? 'btn-warning' : 'btn-success',
                        )}
                        disabled={hasErrors}
                      >
                        Kontynuuj
                      </button>
                    </>
                  ) : (
                    <>
                      <a
                        className="btn btn-md btn-success"
                        href={stepsApi.importSummaryDownload(
                          app.activeProject.id,
                        )}
                        download={true}
                      >
                        Eksport do pliku CSV
                      </a>
                      <button
                        className="btn btn-info btn-md"
                        onClick={handleVisualizationBtn}
                      >
                        Wizualizacja
                      </button>
                      <button
                        className="btn btn-primary btn-md"
                        onClick={handleModelBtn}
                      >
                        Trening Modelu
                      </button>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
          {continueAlertOpen && !isLoading && (
            <InnerModal>
              <ContinueAlert
                setIsLoading={setIsLoading}
                setIsOpen={setContinueAlertOpen}
              />
            </InnerModal>
          )}
        </>
      }
    ></Layout>
  );
}

function ContinueAlert({ setIsOpen, setIsLoading }) {
  const app = useContext(AppContext);
  const stepsApi = app.APIs.steps;

  const handleContinueBtn = async () => {
    setIsLoading(true);
    stepsApi.importSummaryConfirm(app.activeProject.id).then((projectData) => {
      app.setActiveProject(projectData);
      setIsOpen(false);
      setIsLoading(false);
    });
  };
  const handleCancelBtn = () => {
    setIsOpen(false);
  };

  return (
    <div className="flex flex-col w-full h-full">
      <h2 className="mt-2">Czy chcesz kontynuować mimo ostrzeżeń?</h2>
      <div className="flex justify-between">
        <button onClick={handleCancelBtn} className="btn">
          Anuluj
        </button>
        <button onClick={handleContinueBtn} className="btn btn-warning">
          Kontynuuj
        </button>
      </div>
    </div>
  );
}

const preFormatNanPercent = (value) => {
  return Math.round(value * 10) / 10;
};
